/**
 * BL-product-addons.js
 *
 * Keeps product add-ons aligned with the same bundle contract used by the
 * washbasin selector so price UI, sticky ATC, and cart removal stay in sync.
 */

(function () {
  'use strict';

  const SELECTORS = {
    addonsContainer: '[data-product-addons]',
    toggle: '.product-addons__toggle',
    item: '.product-addons__item',
  };

  const activeInstances = new Set();

  function getCartAddonInterceptorRegistry() {
    if (!window.__blCartAddonInterceptor) {
      window.__blCartAddonInterceptor = {
        installed: false,
        originalFetch: window.fetch.bind(window),
        providers: new Map(),
      };
    }
    return window.__blCartAddonInterceptor;
  }

  function installCartAddonFetchInterceptor() {
    const registry = getCartAddonInterceptorRegistry();
    if (registry.installed) return registry;

    registry.installed = true;

    window.fetch = function (url, config) {
      const requestUrl = typeof url === 'string' ? url : url?.url || '';

      if (!requestUrl.includes('/cart/add') || !config) {
        return registry.originalFetch.call(this, url, config);
      }

      const providers = Array.from(registry.providers.values()).sort(
        (a, b) => (b.priority || 0) - (a.priority || 0)
      );

      for (const provider of providers) {
        if (!provider || typeof provider.getBundlePayload !== 'function') continue;

        // 2026-08-25 (hardening): un throw aquí saldría SÍNCRONO por window.fetch, y los
        // fetch().then().catch() del tema no capturan un throw síncrono → el botón se quedaría
        // en is-loading indefinidamente. Un provider roto degrada a "sin bundle".
        let payload = null;
        try {
          payload = provider.getBundlePayload(config);
        } catch (error) {
          payload = null;
        }
        if (!payload || !Array.isArray(payload.items) || !payload.items.length) continue;

        const newConfig = Object.assign({}, config);
        newConfig.headers = Object.assign({}, config.headers || {}, {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        });
        newConfig.body = JSON.stringify({
          items: payload.items,
          sections: payload.sections || '',
          sections_url: payload.sectionsUrl || window.location.pathname,
        });

        const responsePromise = registry.originalFetch.call(this, url, newConfig);

        if (typeof provider.onSuccess === 'function') {
          return responsePromise.then((response) => {
            if (response.ok) provider.onSuccess(response, { payload, config });
            return response;
          });
        }

        return responsePromise;
      }

      return registry.originalFetch.call(this, url, config);
    };

    return registry;
  }

  function buildStableBundleId(mainVariantId, linkedVariantIds) {
    const mainId = String(mainVariantId || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const linkedIds = (Array.isArray(linkedVariantIds) ? linkedVariantIds : [linkedVariantIds])
      .map((id) => String(id || '').replace(/[^a-zA-Z0-9_-]/g, ''))
      .filter(Boolean)
      .sort();

    if (!mainId || !linkedIds.length) return `wb_${Date.now()}`;
    return `wb_${mainId}_${linkedIds.join('_')}`;
  }

  function getProductAddonStateStore() {
    if (!window.__blProductAddonsStateStore) {
      window.__blProductAddonsStateStore = {
        selections: new Map(),
      };
    }

    return window.__blProductAddonsStateStore;
  }

  class ProductAddonsController {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId || '';
      this.productId = container.dataset.productId || '';
      this._fetchProviderId = `product-addons:${this.sectionId}:${Math.random().toString(36).slice(2)}`;
      // 2026-08-26: sello de GENERACIÓN por sección (mismo patrón que el selector v3, 16c7a78).
      // El swap del grouping (viewTransition del tema) mantiene la sección vieja y la nueva
      // CONVIVIENDO en el DOM durante la transición, así que container.isConnected no basta para
      // detectar a la instancia superseded: su provider y sus handlers escriben DOM compartido
      // (#price-<sectionId> — MISMO id entre productos de la misma plantilla — y el sticky global).
      // Cada instancia nueva releva a la anterior de su sección en el momento de montarse.
      const gens = (window.__blAddonsGen = window.__blAddonsGen || {});
      this._gen = (gens[this.sectionId] || 0) + 1;
      gens[this.sectionId] = this._gen;

      this.init();
    }

    init() {
      if (!this.container.isConnected) return;

      this.restorePersistedSelections();   // re-marca los toggles tras un swap del grouping (DOM recreado)
      this.syncAddonSizesToMueble();       // A6: casa la variante del espejo con el Breite del mueble
      this.syncActiveItemStates();
      this.interceptFetch();
      this.bindEvents();
      this.syncBundleUI(this.storeSelectionState());
    }

    // A6 (Victor 2026-07-07): el ESPEJO se ofrece en la variante cuyo tamaño coincide con el Breite
    // del mueble elegido. Cada item con data-addon-sizes (mapa talla→{id,price,available}) apunta su
    // variante/precio a la talla actual del mueble (window.__blWb3.size o el <select> de variante).
    // No-op para addons sin data-addon-sizes (grifos, otros) ni fuera de la PDP de muebles v3.
    muebleSizeDigits() {
      var wb3 = window.__blWb3;
      var raw = (wb3 && wb3.size) || '';
      if (!raw) {
        var sel = this.getMainProductRoot();
        var el = sel && sel.querySelector('variant-selects select, .product-form__input select');
        if (el) raw = el.value || '';
      }
      var m = String(raw).match(/\d+/);
      return m ? m[0] : '';
    }
    syncAddonSizesToMueble() {
      var size = this.muebleSizeDigits(); if (!size) return;
      this.container.querySelectorAll(`${SELECTORS.item}[data-addon-sizes]`).forEach((item) => {
        var map; try { map = JSON.parse(item.getAttribute('data-addon-sizes') || '{}'); } catch (e) { return; }
        var hit = null;
        Object.keys(map).forEach((k) => { if (!hit && String(k).match(/\d+/) && String(k).match(/\d+/)[0] === size) hit = map[k]; });
        if (!hit || !hit.id) return;
        var vid = String(hit.id);
        item.setAttribute('data-addon-variant-id', vid);
        item.setAttribute('data-addon-price', hit.price != null ? hit.price : item.getAttribute('data-addon-price'));
        var toggle = item.querySelector(SELECTORS.toggle);
        if (toggle) toggle.setAttribute('data-addon-variant-id', vid);
        var priceEl = item.querySelector('.product-addons__price');
        if (priceEl && hit.price != null && this.formatMoney) priceEl.textContent = this.formatMoney(parseInt(hit.price, 10) || 0);
      });
    }

    // El grouping recrea el DOM desde HTML cacheado del servidor → los toggles vuelven sin marcar.
    // Restauramos los que estaban ON leyendo el último estado guardado (clave sectionId, estable
    // entre hermanos; el productId cambia). Solo re-marca los addons cuya variante siga existiendo;
    // si el hermano no ofrece ese addon, el toggle no existe y cae a default (regla de no-reset).
    restorePersistedSelections() {
      const store = getProductAddonStateStore();
      const prev = store.selections.get(String(this.sectionId));
      if (!prev || !Array.isArray(prev.selections) || !prev.selections.length) return;
      const wanted = new Set(prev.selections.map((s) => String(s.variantId)));
      this.container.querySelectorAll(SELECTORS.item).forEach((item) => {
        const toggle = item.querySelector(SELECTORS.toggle);
        if (!toggle || toggle.checked) return;
        const vid = String(item.dataset.addonVariantId || '').trim();
        if (vid && wanted.has(vid)) toggle.checked = true;
      });
    }

    bindEvents() {
      this._boundToggleChange = this.handleToggleChange.bind(this);
      this.container.addEventListener('change', this._boundToggleChange);

      this._boundVariantChanged = this.handleVariantChanged.bind(this);
      document.addEventListener('variant:changed', this._boundVariantChanged);

      if (window.FoxTheme?.pubsub?.subscribe) {
        this.variantChangeUnsubscriber = window.FoxTheme.pubsub.subscribe(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.handleVariantPubsub.bind(this)
        );
      }

      // A6: el selector v3 avisa cambios de talla/modo del mueble → re-casar la variante del espejo
      this._boundWb3Change = () => {
        if (!this.ensureConnected()) return;
        this.syncAddonSizesToMueble();
        this.syncBundleUI(this.storeSelectionState());
      };
      document.addEventListener('bl:wb3:change', this._boundWb3Change);
    }

    destroy() {
      if (this._destroyed) return;
      this._destroyed = true;

      if (this._variantTimer) {
        clearTimeout(this._variantTimer);
      }

      if (this._boundToggleChange) {
        this.container.removeEventListener('change', this._boundToggleChange);
      }

      if (this._boundVariantChanged) {
        document.removeEventListener('variant:changed', this._boundVariantChanged);
      }

      if (this._boundWb3Change) {
        document.removeEventListener('bl:wb3:change', this._boundWb3Change);
      }

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      const registry = getCartAddonInterceptorRegistry();
      registry.providers.delete(this._fetchProviderId);

      if (this.container.__blProductAddonsInstance === this) {
        delete this.container.__blProductAddonsInstance;
      }

      activeInstances.delete(this);
    }

    ensureConnected() {
      const gens = (window.__blAddonsGen = window.__blAddonsGen || {});
      if (this.container.isConnected && gens[this.sectionId] === this._gen) return true;
      this.destroy();
      return false;
    }

    getMainProductRoot() {
      return (
        document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
        document.querySelector(`product-info[data-original-section="${this.sectionId}"]`) ||
        this.container.closest('product-info') ||
        document.querySelector('product-info[id^="MainProduct-"]') ||
        document.querySelector('product-info')
      );
    }

    getProductForm() {
      const root = this.getMainProductRoot();

      return (
        root?.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
        root?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
        document.querySelector('.main-product-form') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    getSelectedMainVariant() {
      const root = this.getMainProductRoot();
      const variantScript = root?.querySelector(
        'variant-selects [data-selected-variant], variant-radios [data-selected-variant]'
      );

      if (!variantScript?.textContent) return null;

      try {
        return JSON.parse(variantScript.textContent);
      } catch (error) {
        return null;
      }
    }

    getCurrentMainVariantId() {
      const variant = this.getSelectedMainVariant();
      if (variant?.id) return String(variant.id);

      return String(this.getProductForm()?.querySelector('[name="id"]')?.value || '');
    }

    getBasePrice() {
      return parseInt(this.getSelectedMainVariant()?.price || 0, 10) || 0;
    }

    getBaseCompareAtPrice() {
      return parseInt(this.getSelectedMainVariant()?.compare_at_price || 0, 10) || 0;
    }

    formatMoney(cents) {
      return window.BLBundlePriceState?.formatMoney(cents)
        ?? (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).replace(',00', '');
    }

    buildBundlePriceState(basePrice, addonPrice, addonRegularPrice = addonPrice) {
      return window.BLBundlePriceState.buildPriceState(basePrice, addonPrice, {
        baseCompareAtPrice: this.getBaseCompareAtPrice(),
        addonRegularPrice,
      });
    }

    getPriceTargets() {
      const targets = [];
      const mainPrice = document.querySelector(`#price-${this.sectionId}`);
      if (mainPrice) targets.push(mainPrice);

      document
        .querySelectorAll('#shopify-section-sticky-atc-bar sticky-atc-bar .sticky-atc-bar__product-info')
        .forEach((target) => {
          if (target.querySelector('.f-price')) targets.push(target);
        });

      return targets;
    }

    getButtonTargets() {
      const targets = [];
      const seen = new Set();

      const addIfPresent = (button) => {
        if (!button || seen.has(button)) return;
        seen.add(button);
        targets.push(button);
      };

      addIfPresent(document.querySelector(`#ProductSubmitButton-${this.sectionId}`));

      const mainForm = document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`);
      addIfPresent(mainForm?.querySelector('.product-form__submit'));

      document
        .querySelectorAll('#shopify-section-sticky-atc-bar .product-form__submit')
        .forEach(addIfPresent);

      return targets;
    }

    getCompanionWashbasin() {
      const sectionSelector = this.sectionId
        ? `[data-washbasin-selector-v2][data-section-id="${this.sectionId}"]`
        : '[data-washbasin-selector-v2]';
      const root = this.container.closest('.shopify-section') || document;
      // v2 primero; si no, el selector V3 ([data-bl-wb3]) — mismo contrato __blWbInstance.
      // (2026-07-04: sin esto, en la PDP v3 este bloque se creía standalone y COMPETÍA por el precio)
      const washbasinNode = root.querySelector(sectionSelector) || document.querySelector(sectionSelector)
        || root.querySelector('[data-bl-wb3]') || document.querySelector('[data-bl-wb3]');

      return washbasinNode?.__blWbInstance || null;
    }

    createButtonPriceWrapper(button) {
      const btnTextSpan = button.querySelector('.btn__text');
      if (!btnTextSpan) return null;

      let wrapper = button.querySelector('[data-btn-price-wrapper]');
      if (!wrapper) {
        wrapper = document.createElement('span');
        wrapper.setAttribute('data-btn-price-wrapper', '');
        wrapper.className = 'btn__price-arrow-wrapper';
        wrapper.innerHTML = `
          <span class="btn__price-separator">|</span>
          <span data-btn-price-display></span>
          <svg class="btn__price-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        `;
        btnTextSpan.parentNode.insertBefore(wrapper, btnTextSpan.nextSibling);
      }

      return wrapper;
    }

    getAddonRecord(item) {
      if (!item) return null;

      const variantId = String(item.dataset.addonVariantId || '').trim();
      if (!variantId) return null;

      const title =
        (item.dataset.addonTitle || item.querySelector('.product-addons__title')?.textContent || '').trim();
      const image = (item.dataset.addonImage || item.querySelector('.product-addons__img')?.currentSrc || '').trim();
      const price = parseInt(item.dataset.addonPrice || 0, 10) || 0;
      const compareAtPrice = parseInt(item.dataset.addonCompareAtPrice || 0, 10) || 0;

      return {
        variantId,
        productId: String(item.dataset.addonProductId || '').trim(),
        title,
        image,
        price,
        compareAtPrice,
        vendor: String(item.dataset.addonVendor || '').trim(),
        type: String(item.dataset.addonType || '').trim(),
        handle: String(item.dataset.addonHandle || '').trim(),
        variantTitle: String(item.dataset.addonVariantTitle || '').trim(),
        sku: String(item.dataset.addonVariantSku || '').trim(),
        url: String(item.dataset.addonUrl || '').trim(),
        currencyCode: String(item.dataset.currencyCode || '').trim(),
      };
    }

    publishAddonViewItem(record, item) {
      if (!record?.productId || !record?.variantId) return;

      document.dispatchEvent(
        new CustomEvent('bl:product-addons:view-item', {
          bubbles: true,
          detail: {
            source: 'product-addons',
            item,
            productId: record.productId,
            productHandle: record.handle,
            productTitle: record.title,
            productVendor: record.vendor,
            productType: record.type,
            variantId: record.variantId,
            variantTitle: record.variantTitle,
            sku: record.sku,
            priceCents: record.price,
            compareAtPriceCents: record.compareAtPrice,
            image: record.image,
            url: record.url,
            currencyCode: record.currencyCode,
          },
        })
      );
    }

    getSelectedAddonRecords() {
      return Array.from(this.container.querySelectorAll(`${SELECTORS.toggle}:checked`))
        .map((toggle) => this.getAddonRecord(toggle.closest(SELECTORS.item)))
        .filter(Boolean);
    }

    buildSelectionState() {
      const selections = this.getSelectedAddonRecords();
      const addonPrice = selections.reduce((total, selection) => total + selection.price, 0);
      const addonCompareAtPrice = selections.reduce(
        (total, selection) => total + selection.compareAtPrice,
        0
      );
      const addonRegularPrice = selections.reduce(
        (total, selection) => total + (
          selection.compareAtPrice > selection.price ? selection.compareAtPrice : selection.price
        ),
        0
      );
      const addonNames = selections.map((selection) => selection.title).filter(Boolean);

      return {
        sectionId: this.sectionId || null,
        productId: this.productId || null,
        hasAddons: selections.length > 0,
        selections,
        addonVariantIds: selections.map((selection) => selection.variantId),
        addonProductIds: selections.map((selection) => selection.productId).filter(Boolean),
        addonNames,
        addonName: addonNames.join(' + '),
        addonImage: selections.find((selection) => selection.image)?.image || '',
        addonImages: selections.map((selection) => selection.image).filter(Boolean),
        addonPrice,
        addonCompareAtPrice,
        addonRegularPrice,
      };
    }

    storeSelectionState(state = this.buildSelectionState()) {
      const store = getProductAddonStateStore();
      const keys = [this.sectionId, this.productId].filter(Boolean).map(String);

      if (!keys.length) {
        keys.push('default');
      }

      keys.forEach((key) => {
        store.selections.set(key, state);
      });

      return state;
    }

    publishStickyState(detail = {}) {
      const stickyDetail = Object.assign(
        {
          sectionId: this.sectionId || null,
          productId: this.productId || null,
          hasBasin: false,
          basinName: '',
          basinImage: '',
          hasAddons: false,
          addonName: '',
          addonNames: [],
          addonImage: '',
          addonImages: [],
          addonVariantIds: [],
          addonPrice: 0,
        },
        detail
      );

      window.__blProductAddonsStickyState = window.__blProductAddonsStickyState || {};
      const cacheKey = String(stickyDetail.sectionId || stickyDetail.productId || 'default');
      window.__blProductAddonsStickyState[cacheKey] = stickyDetail;

      document.dispatchEvent(
        new CustomEvent('bl:product-addons:sticky-update', {
          bubbles: true,
          detail: stickyDetail,
        })
      );
    }

    syncActiveItemStates() {
      this.container.querySelectorAll(SELECTORS.item).forEach((item) => {
        const toggle = item.querySelector(SELECTORS.toggle);
        item.classList.toggle('is-active', Boolean(toggle?.checked));
      });
    }

    handleToggleChange(event) {
      if (!event.target.matches(SELECTORS.toggle)) return;
      if (!this.ensureConnected()) return;

      const item = event.target.closest(SELECTORS.item);
      if (item) {
        item.classList.toggle('is-active', event.target.checked);
        if (event.target.checked) {
          this.publishAddonViewItem(this.getAddonRecord(item), item);
        }
      }

      this.syncBundleUI(this.storeSelectionState());
    }

    handleVariantPubsub(event) {
      if (!this.ensureConnected()) return;

      const sectionId = event?.data?.sectionId || event?.sectionId || '';
      if (sectionId && String(sectionId) !== String(this.sectionId)) return;

      this.syncBundleUI(this.storeSelectionState());
    }

    handleVariantChanged() {
      if (!this.ensureConnected()) return;

      clearTimeout(this._variantTimer);
      this._variantTimer = setTimeout(() => {
        if (!this.ensureConnected()) return;
        this.syncAddonSizesToMueble();   // A6: re-casar la variante del espejo con la talla del mueble
        this.syncBundleUI(this.storeSelectionState());
      }, 0);
    }

    applyStandalonePriceState(state) {
      const addonPrice = state.addonPrice || 0;
      const addonRegularPrice = state.addonRegularPrice || addonPrice;
      const basePrice = this.getBasePrice();
      const bundlePriceState = this.buildBundlePriceState(basePrice, addonPrice, addonRegularPrice);
      const formattedTotal = bundlePriceState.displayPrice;

      this.getPriceTargets().forEach((target) => {
        if (addonPrice > 0) {
          window.BLBundlePriceState?.applyPriceNode?.(target, bundlePriceState, { lock: false });
        } else {
          window.BLBundlePriceState?.clearPriceNode?.(target);
        }
      });

      const nativePriceSection = document.querySelector(`#price-${this.sectionId}`);
      const nativePriceEl = nativePriceSection
        ? nativePriceSection.querySelector('.f-price-item--regular, .price-item--regular, .f-price__regular .money')
        : null;
      const priceForButton = addonPrice > 0 ? formattedTotal : nativePriceEl?.textContent?.trim() || null;

      if (addonPrice > 0) {
        window.BLAtcPriceSync?.applyBundlePrice('product-addons', formattedTotal);
      } else {
        window.BLAtcPriceSync?.clearBundlePrice('product-addons');
      }

      window.BLBundlePriceState?.setOwnerState?.('product-addons', addonPrice > 0, {
        buttonReady: addonPrice > 0 && Boolean(priceForButton),
      });

      if (priceForButton) {
        this.getButtonTargets().forEach((button) => {
          const wrapper = this.createButtonPriceWrapper(button);
          if (!wrapper) return;

          wrapper.dataset.btnPriceSource = addonPrice > 0 ? 'bundle' : 'default';
          const priceDisplay = wrapper.querySelector('[data-btn-price-display]');
          if (priceDisplay) {
            priceDisplay.textContent = priceForButton;
          }
        });
      }

      this.publishStickyState({
        hasAddons: state.hasAddons,
        addonName: state.addonName,
        addonNames: state.addonNames,
        addonImage: state.addonImage,
        addonImages: state.addonImages,
        addonVariantIds: state.addonVariantIds,
        addonPrice,
        addonRegularPrice,
        mainVariantId: this.getCurrentMainVariantId() || null,
        bundleTotal: basePrice + addonPrice,
        bundleTotalFormatted: addonPrice > 0 ? formattedTotal : priceForButton || '',
        bundleCompareAt: bundlePriceState.compareAtTotal,
        bundleCompareAtFormatted: bundlePriceState.compareAtPrice,
      });
    }

    syncBundleUI(state = this.buildSelectionState()) {
      const storedState = this.storeSelectionState(state);
      const washbasinInstance = this.getCompanionWashbasin();

      if (washbasinInstance && typeof washbasinInstance.scheduleGlobalPriceUpdate === 'function') {
        window.BLAtcPriceSync?.clearBundlePrice('product-addons');
        window.BLBundlePriceState?.setOwnerState?.('product-addons', false);
        washbasinInstance.scheduleGlobalPriceUpdate();
        return;
      }

      this.applyStandalonePriceState(storedState);
    }

    buildBundlePayload(config) {
      if (!this.ensureConnected()) return null;
      if (this.getCompanionWashbasin()) return null;

      const selectionState = this.buildSelectionState();
      if (!selectionState.hasAddons) return null;

      let items = [];
      let sections = '';
      let sectionsUrl = '';
      let bundleId = null;

      if (config.body instanceof FormData) {
        const formData = config.body;
        const mainId = formData.get('id');
        if (!mainId) return null;

        const mainQty = formData.get('quantity') || 1;
        const bundleQty = parseInt(mainQty, 10) || 1;
        sections = formData.get('sections') || '';
        sectionsUrl = formData.get('sections_url') || window.location.pathname;
        bundleId = buildStableBundleId(mainId, selectionState.addonVariantIds);

        const mainProps = {
          _msw_bundle_id: bundleId,
          _linkedTo: selectionState.addonVariantIds.join(','),
        };

        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[')) continue;
          const propNameMatch = key.match(/properties\[(.*?)\]/);
          if (!propNameMatch?.[1]) continue;
          mainProps[propNameMatch[1]] = value;
        }

        items = [
          {
            id: parseInt(mainId, 10),
            quantity: bundleQty,
            properties: mainProps,
          },
          ...selectionState.addonVariantIds.map((variantId) => ({
            id: parseInt(variantId, 10),
            quantity: bundleQty,
            properties: {
              _msw_bundle_id: bundleId,
              _isAddon: 'true',
              _linkedTo: mainId,
            },
          })),
        ];
      } else if (typeof config.body === 'string') {
        try {
          const body = JSON.parse(config.body);
          const mainId = body.id || body.items?.[0]?.id;
          if (!mainId) return null;

          const mainQty = body.quantity || body.items?.[0]?.quantity || 1;
          const bundleQty = parseInt(mainQty, 10) || 1;
          sections = body.sections || '';
          sectionsUrl = body.sections_url || window.location.pathname;
          bundleId = buildStableBundleId(mainId, selectionState.addonVariantIds);

          const mainProps = Object.assign({}, body.properties || body.items?.[0]?.properties || {});
          mainProps._msw_bundle_id = bundleId;
          mainProps._linkedTo = selectionState.addonVariantIds.join(',');

          items = [
            {
              id: parseInt(mainId, 10),
              quantity: bundleQty,
              properties: mainProps,
            },
            ...selectionState.addonVariantIds.map((variantId) => ({
              id: parseInt(variantId, 10),
              quantity: bundleQty,
              properties: {
                _msw_bundle_id: bundleId,
                _isAddon: 'true',
                _linkedTo: mainId,
              },
            })),
          ];
        } catch (error) {
          return null;
        }
      }

      if (!items.length) return null;

      // orden en carrito (Shopify lista de mas reciente a mas antiguo): invertido para que el
      // mueble encabece el conjunto y los espejos lo cierren — mismo criterio que los providers v3
      items.reverse();
      return { items, sections, sectionsUrl };
    }

    interceptFetch() {
      const registry = installCartAddonFetchInterceptor();

      registry.providers.set(this._fetchProviderId, {
        priority: 10,
        getBundlePayload: (config) => this.buildBundlePayload(config),
        onSuccess: (response, meta = {}) => {
          // QA kill-switch (mismo flag que en washbasin-v2). Este onSuccess en la
          // practica nunca se ejecuta porque washbasin-v2 (priority 20) gana el
          // interceptor y product-addons (priority 10) queda excluido por el
          // `return responsePromise` del loop. Se incluye la guarda por simetria
          // y limpieza, en caso de que en el futuro la prioridad cambie.
          const _killSwitch = new URLSearchParams(window.location.search).get('bl_no_cart_refresh') === 'on';
          if (_killSwitch) {
            console.log('%c[BL-killswitch] cart:refresh SUPPRESSED (product-addons)', 'color:#fff;background:#dc2626;padding:2px 6px;border-radius:3px');
            return;
          }
          if (meta?.payload?.sections) return;
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { open: true },
            })
          );
        },
      });
    }
  }

  function cleanupDisconnectedInstances() {
    activeInstances.forEach((instance) => {
      instance.ensureConnected();   // destruye desconectadas Y relevadas por una generación más nueva
    });
  }

  function init() {
    cleanupDisconnectedInstances();

    document.querySelectorAll(SELECTORS.addonsContainer).forEach((container) => {
      if (container.__blProductAddonsInstance) return;

      const instance = new ProductAddonsController(container);
      container.__blProductAddonsInstance = instance;
      activeInstances.add(instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('bl:grouping:swapped', init);
})();
